import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { sendApiRequest } from '../utils';
import { toast } from 'react-toastify';
import {
  FiUsers,
  FiUserCheck,
  FiUserX,
  FiSearch,
  FiFilter,
  FiDownload,
  FiRefreshCw,
  FiMapPin,
  FiCalendar,
  FiActivity,
  FiTrendingUp,
  FiAward,
  FiStar,
  FiChevronDown,
  FiChevronUp,
  FiInfo
} from 'react-icons/fi';
import {
  FaUserCircle,
  FaCrown,
  FaMedal,
  FaCertificate
} from 'react-icons/fa';

const MyDirectNetwork = () => {
  const [networkData, setNetworkData] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortConfig, setSortConfig] = useState({ key: 'memberId', direction: 'asc' });
  const [selectedMember, setSelectedMember] = useState(null);
  const [showModal, setShowModal] = useState(false);

  useEffect(() => {
    fetchNetworkData();
  }, []);

  const fetchNetworkData = async () => {
    try {
      setIsLoading(true);
      const response = await sendApiRequest('api/network/my-direct-network', {}, 'GET');
      
      if (response.data.success) {
        setNetworkData(response.data.data);
      } else {
        toast.error(response.message);
      }
    } catch (error) {
      toast.error('Failed to fetch network data');
      console.error(error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  const sortedData = React.useMemo(() => {
    if (!sortConfig.key) return networkData;

    return [...networkData].sort((a, b) => {
      let aVal = a[sortConfig.key];
      let bVal = b[sortConfig.key];
      
      if (sortConfig.key === 'regDate' || sortConfig.key === 'actDate') {
        aVal = new Date(aVal);
        bVal = new Date(bVal);
      }
      
      if (aVal < bVal) return sortConfig.direction === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortConfig.direction === 'asc' ? 1 : -1;
      return 0;
    });
  }, [networkData, sortConfig]);

  const filteredData = sortedData.filter(member => 
    member.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    member.memberId?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    member.city?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    member.email?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const getSortIcon = (key) => {
    if (sortConfig.key !== key) return <FiChevronDown className="w-3 h-3 opacity-30" />;
    return sortConfig.direction === 'asc' ? <FiChevronUp className="w-3 h-3" /> : <FiChevronDown className="w-3 h-3" />;
  };

  const getStatusColor = (status) => {
    return status === 'active' 
      ? 'bg-emerald-100 text-emerald-700 border-emerald-200' 
      : 'bg-red-100 text-red-700 border-red-200';
  };

  const getStatusIcon = (status) => {
    return status === 'active' ? <FiUserCheck className="w-3 h-3" /> : <FiUserX className="w-3 h-3" />;
  };

  const activeCount = networkData.filter(m => m.status === 'active').length;
  const inactiveCount = networkData.filter(m => m.status === 'inactive').length;
  const totalBusiness = networkData.reduce((sum, m) => sum + (m.business || 0), 0);

  const handleRefresh = () => {
    fetchNetworkData();
    toast.info('Refreshing network data...');
  };

  const handleExport = () => {
    const csvData = filteredData.map(member => ({
      'Member ID': member.memberId,
      'Name': member.name,
      'Email': member.email,
      'Phone': member.phone,
      'City': member.city,
      'Registration Date': member.regDate,
      'Activation Date': member.actDate || 'Not Activated',
      'Status': member.status,
      'Business': member.business || 0
    }));
    
    const csv = convertToCSV(csvData);
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `direct-network-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
    toast.success('Export started!');
  };

  const convertToCSV = (data) => {
    const headers = Object.keys(data[0] || {});
    const rows = data.map(obj => headers.map(header => JSON.stringify(obj[header] || '')).join(','));
    return [headers.join(','), ...rows].join('\n');
  };

  const openMemberDetails = (member) => {
    setSelectedMember(member);
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setSelectedMember(null);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-indigo-50/30 to-purple-50/30 py-6 sm:py-10">
      <div className="container mx-auto px-4 max-w-7xl">
        {/* Header */}
        <div className="mb-8">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <div className="flex items-center gap-2 mb-2">
                <div className="w-1 h-8 bg-gradient-to-b from-indigo-500 to-purple-500 rounded-full"></div>
                <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold bg-gradient-to-r from-indigo-600 to-purple-600 bg-clip-text text-transparent">
                  My Direct Network
                </h1>
              </div>
              <p className="text-gray-500 mt-1 text-sm">Manage and view your direct team members</p>
            </div>
            <div className="flex items-center gap-3">
              <button
                onClick={handleRefresh}
                className="p-2.5 bg-white rounded-xl shadow-md hover:shadow-lg transition text-gray-600 hover:text-indigo-600"
                title="Refresh"
              >
                <FiRefreshCw className="w-5 h-5" />
              </button>
              <button
                onClick={handleExport}
                className="p-2.5 bg-white rounded-xl shadow-md hover:shadow-lg transition text-gray-600 hover:text-indigo-600"
                title="Export to CSV"
              >
                <FiDownload className="w-5 h-5" />
              </button>
              <span className="flex items-center gap-2 px-3 py-1.5 bg-emerald-100 rounded-full">
                <span className="w-2 h-2 bg-emerald-500 rounded-full animate-pulse"></span>
                <span className="text-xs font-semibold text-emerald-700">Live Network</span>
              </span>
            </div>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 mb-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="bg-white rounded-2xl p-5 shadow-lg border border-gray-100"
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Total Members</p>
                <p className="text-3xl font-bold text-gray-800">{networkData.length}</p>
                <p className="text-xs text-gray-400 mt-1">Direct downline</p>
              </div>
              <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-indigo-500 to-purple-500 flex items-center justify-center shadow-lg">
                <FiUsers className="w-7 h-7 text-white" />
              </div>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="bg-white rounded-2xl p-5 shadow-lg border border-gray-100"
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Active Members</p>
                <p className="text-3xl font-bold text-emerald-600">{activeCount}</p>
                <p className="text-xs text-gray-400 mt-1">{((activeCount/networkData.length)*100 || 0).toFixed(1)}% of total</p>
              </div>
              <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-emerald-500 to-teal-500 flex items-center justify-center shadow-lg">
                <FiUserCheck className="w-7 h-7 text-white" />
              </div>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="bg-white rounded-2xl p-5 shadow-lg border border-gray-100"
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Inactive Members</p>
                <p className="text-3xl font-bold text-red-600">{inactiveCount}</p>
                <p className="text-xs text-gray-400 mt-1">Need activation</p>
              </div>
              <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-red-500 to-rose-500 flex items-center justify-center shadow-lg">
                <FiUserX className="w-7 h-7 text-white" />
              </div>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
            className="bg-white rounded-2xl p-5 shadow-lg border border-gray-100"
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Total Business</p>
                <p className="text-3xl font-bold text-purple-600">₹{totalBusiness.toLocaleString()}</p>
                <p className="text-xs text-gray-400 mt-1">Team volume</p>
              </div>
              <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center shadow-lg">
                <FiTrendingUp className="w-7 h-7 text-white" />
              </div>
            </div>
          </motion.div>
        </div>

        {/* Search and Filter Bar */}
        <div className="bg-white rounded-2xl shadow-lg border border-gray-100 mb-6 overflow-hidden">
          <div className="p-5">
            <div className="flex flex-col sm:flex-row gap-4">
              <div className="flex-1 relative">
                <FiSearch className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search by name, ID, email, or city..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-11 pr-4 py-3 rounded-xl border border-gray-200 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-200 transition-all outline-none"
                />
              </div>
              <div className="flex gap-2">
                <button className="px-4 py-3 rounded-xl border border-gray-200 text-gray-600 hover:border-indigo-300 hover:text-indigo-600 transition flex items-center gap-2">
                  <FiFilter className="w-4 h-4" />
                  Filter
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Members Table */}
        <div className="bg-white rounded-2xl shadow-xl overflow-hidden border border-gray-100">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gradient-to-r from-indigo-50 to-purple-50">
                <tr>
                  {[
                    { key: 'memberId', label: 'Member ID', icon: FiUsers },
                    { key: 'name', label: 'Name', icon: FaUserCircle },
                    { key: 'city', label: 'City', icon: FiMapPin },
                    { key: 'regDate', label: 'Reg. Date', icon: FiCalendar },
                    { key: 'actDate', label: 'Act. Date', icon: FiActivity },
                    { key: 'status', label: 'Status', icon: FiActivity },
                    { key: 'actions', label: 'Actions', icon: null }
                  ].map((column) => (
                    <th
                      key={column.key}
                      onClick={() => column.key !== 'actions' && handleSort(column.key)}
                      className={`px-4 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider ${
                        column.key !== 'actions' ? 'cursor-pointer hover:bg-white/50 transition' : ''
                      }`}
                    >
                      <div className="flex items-center gap-2">
                        {column.icon && <column.icon className="w-3 h-3" />}
                        {column.label}
                        {column.key !== 'actions' && getSortIcon(column.key)}
                      </div>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                <AnimatePresence>
                  {isLoading ? (
                    <tr>
                      <td colSpan="7" className="text-center py-16">
                        <div className="flex flex-col items-center justify-center">
                          <div className="relative">
                            <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-indigo-500"></div>
                            <div className="absolute inset-0 flex items-center justify-center">
                              <FiUsers className="w-5 h-5 text-indigo-400 animate-pulse" />
                            </div>
                          </div>
                          <p className="mt-4 text-gray-500 font-medium">Loading network data...</p>
                        </div>
                      </td>
                    </tr>
                  ) : filteredData.length === 0 ? (
                    <tr>
                      <td colSpan="7" className="text-center py-16">
                        <div className="flex flex-col items-center justify-center">
                          <div className="w-20 h-20 mb-4 bg-gray-100 rounded-full flex items-center justify-center">
                            <FiUsers className="w-10 h-10 text-gray-400" />
                          </div>
                          <p className="text-gray-500 font-medium text-lg">No members found</p>
                          <p className="text-sm text-gray-400 mt-1">Try adjusting your search criteria</p>
                        </div>
                      </td>
                    </tr>
                  ) : (
                    filteredData.map((member, index) => (
                      <motion.tr
                        key={member.memberId}
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -10 }}
                        transition={{ delay: index * 0.02 }}
                        className="hover:bg-gray-50 transition-colors cursor-pointer"
                        onClick={() => openMemberDetails(member)}
                      >
                        <td className="px-4 py-4">
                          <span className="font-mono text-sm font-semibold text-indigo-600">
                            {member.memberId}
                          </span>
                        </td>
                        <td className="px-4 py-4">
                          <div className="flex items-center gap-3">
                            <div className="w-8 h-8 rounded-full bg-gradient-to-br from-indigo-100 to-purple-100 flex items-center justify-center">
                              <FaUserCircle className="w-5 h-5 text-indigo-500" />
                            </div>
                            <span className="font-medium text-gray-800">{member.name}</span>
                          </div>
                        </td>
                        <td className="px-4 py-4">
                          <div className="flex items-center gap-1 text-gray-600">
                            <FiMapPin className="w-3 h-3" />
                            <span className="text-sm">{member.city || 'N/A'}</span>
                          </div>
                        </td>
                        <td className="px-4 py-4 text-sm text-gray-600">
                          {member.regDate ? new Date(member.regDate).toLocaleDateString('en-IN') : '-'}
                        </td>
                        <td className="px-4 py-4 text-sm text-gray-600">
                          {member.actDate ? new Date(member.actDate).toLocaleDateString('en-IN') : '-'}
                        </td>
                        <td className="px-4 py-4">
                          <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-semibold ${getStatusColor(member.status)}`}>
                            {getStatusIcon(member.status)}
                            {member.status === 'active' ? 'Active' : 'Inactive'}
                          </span>
                        </td>
                        <td className="px-4 py-4">
                          <button className="p-2 rounded-lg bg-gray-100 text-gray-600 hover:bg-indigo-100 hover:text-indigo-600 transition">
                            <FiInfo className="w-4 h-4" />
                          </button>
                        </td>
                      </motion.tr>
                    ))
                  )}
                </AnimatePresence>
              </tbody>
            </table>
          </div>

          {/* Footer */}
          {filteredData.length > 0 && (
            <div className="border-t border-gray-100 px-6 py-4 bg-gray-50">
              <div className="flex justify-between items-center text-sm">
                <p className="text-gray-500">
                  Showing {filteredData.length} of {networkData.length} members
                </p>
                <div className="flex gap-2">
                  <button className="px-3 py-1 rounded-lg bg-white border border-gray-200 text-gray-600 hover:bg-gray-50 transition">
                    Previous
                  </button>
                  <button className="px-3 py-1 rounded-lg bg-indigo-600 text-white hover:bg-indigo-700 transition">
                    Next
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Member Details Modal */}
      <AnimatePresence>
        {showModal && selectedMember && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-4" onClick={closeModal}>
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              className="bg-white rounded-2xl max-w-md w-full max-h-[90vh] overflow-y-auto"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="bg-gradient-to-r from-indigo-600 to-purple-600 px-6 py-4 sticky top-0">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 rounded-xl bg-white/20 flex items-center justify-center">
                      <FaUserCircle className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <h2 className="text-white font-semibold text-lg">{selectedMember.name}</h2>
                      <p className="text-indigo-100 text-sm">{selectedMember.memberId}</p>
                    </div>
                  </div>
                  <button onClick={closeModal} className="text-white hover:bg-white/20 rounded-lg p-1 transition">
                    ✕
                  </button>
                </div>
              </div>

              <div className="p-6 space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="bg-gray-50 rounded-xl p-3">
                    <p className="text-xs text-gray-500">Email</p>
                    <p className="text-sm font-medium text-gray-800 break-all">{selectedMember.email || 'N/A'}</p>
                  </div>
                  <div className="bg-gray-50 rounded-xl p-3">
                    <p className="text-xs text-gray-500">Phone</p>
                    <p className="text-sm font-medium text-gray-800">{selectedMember.phone || 'N/A'}</p>
                  </div>
                  <div className="bg-gray-50 rounded-xl p-3">
                    <p className="text-xs text-gray-500">City</p>
                    <p className="text-sm font-medium text-gray-800">{selectedMember.city || 'N/A'}</p>
                  </div>
                  <div className="bg-gray-50 rounded-xl p-3">
                    <p className="text-xs text-gray-500">Status</p>
                    <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-semibold ${getStatusColor(selectedMember.status)}`}>
                      {getStatusIcon(selectedMember.status)}
                      {selectedMember.status === 'active' ? 'Active' : 'Inactive'}
                    </span>
                  </div>
                  <div className="bg-gray-50 rounded-xl p-3">
                    <p className="text-xs text-gray-500">Registration Date</p>
                    <p className="text-sm font-medium text-gray-800">
                      {selectedMember.regDate ? new Date(selectedMember.regDate).toLocaleDateString('en-IN') : '-'}
                    </p>
                  </div>
                  <div className="bg-gray-50 rounded-xl p-3">
                    <p className="text-xs text-gray-500">Activation Date</p>
                    <p className="text-sm font-medium text-gray-800">
                      {selectedMember.actDate ? new Date(selectedMember.actDate).toLocaleDateString('en-IN') : 'Not Activated'}
                    </p>
                  </div>
                </div>

                {selectedMember.business > 0 && (
                  <div className="bg-indigo-50 rounded-xl p-4 border border-indigo-100">
                    <div className="flex items-center justify-between">
                      <p className="text-sm font-semibold text-indigo-800">Total Business</p>
                      <p className="text-2xl font-bold text-indigo-600">₹{selectedMember.business.toLocaleString()}</p>
                    </div>
                  </div>
                )}
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default MyDirectNetwork;